import { canReadWriteCookies, getCookie } from '@/utilities/cookies';
import { useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

type SHA1Hash = Nominal<string, 'sha1'>;
type UUID = Nominal<string, 'uuid'>;
type EventUUID = Nominal<string, 'event-uuid'>;

declare global {
  function ire(
    method: 'identify',
    data:
      | {
          /**
           * Unique user identifier or an empty string
           */
          customerId: string;
          /**
           * SHA1 hash of the customer's email address or an empty string
           */
          customerEmail?: SHA1Hash;
          /**
           * Unique identifier used to identify a visitor on your website (regardless of whether
           * they're signed in).
           */
          customProfileId?: UUID;
        }
      | {
          /**
           * Unique identifier used to identify a visitor on your website (regardless of whether
           * they're signed in).
           */
          customProfileId: UUID;
        }
  ): void;
  function ire(
    method: 'trackConversion',
    actionTrackerId: 60396,
    data: {
      /**
       * Unique trackable ID for each event. (AlphaNumeric 64)
       */
      orderId: EventUUID;
      /**
       * The unique `im_ref` captured on the initial landing page’s query string. This value
       * is used to credit partners.
       */
      clickId: string;
      /**
       * Unique identifier used to identify a visitor on your website (regardless of whether
       * they're signed in).
       */
      customProfileId: UUID;
      /**
       * Unique user identifier or an empty string
       */
      customerId: string;
      /**
       * SHA1 hash of the customer's email address or an empty string
       */
      customerEmail?: SHA1Hash | '';
    },
    options?: { verifySiteDefinitionMatch?: boolean }
  ): void;
  interface Window {
    ire: typeof ire;
  }
}

export const generateSHA1Hash = async (unencrypted: string) => {
  try {
    const enc = new TextEncoder();
    const hash = await crypto.subtle.digest('SHA-1', enc.encode(unencrypted));
    return Array.from(new Uint8Array(hash))
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('') as SHA1Hash;
  } catch (error) {
    // Fallback when crypto.subtle is unavailable (e.g., Android localhost)
    // Return empty string to prevent tracking errors while maintaining type safety
    console.warn('crypto.subtle.digest failed, using fallback:', error);
    return undefined;
  }
};

/**
 * Handles impact referral ID (`im_ref`) get & set.
 *
 * Implicitly performs some side effects to manage current imRef state for browser/device.
 */
export const useImRef = () => {
  const cachedImRef = useRef<string | undefined>(undefined);

  const set = useCallback((imRef: string) => {
    // Expire affiliate referral code in 1 month's time
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    const expiry = date.toUTCString();

    if (canReadWriteCookies()) {
      document.cookie = `_sws_imref=${imRef}; path=/; expires=${expiry}`;
    }

    cachedImRef.current = imRef;
  }, []);

  /**
   * Get the impact referral ID (`im_ref`) from URL search params or a previously stored value in a
   * cookie.
   */
  const get = useCallback(() => {
    try {
      if (cachedImRef.current !== undefined) {
        return cachedImRef.current;
      }

      const imRefSearch = new URLSearchParams(window?.location?.search).get(
        'im_ref'
      );

      if (imRefSearch !== null) {
        set(imRefSearch);
        return imRefSearch;
      }

      const imRefCookie = getCookie('_sws_imref');

      if (imRefCookie !== undefined) {
        cachedImRef.current = imRefCookie;
      }

      return imRefCookie;
    } catch (error) {
      return undefined;
    }
  }, [set]);

  return { get };
};

/**
 * Handles impact session ID (`customProfileId`) get & set.
 *
 * Implicitly performs some side effects to manage current impact session ID state for
 * browser/device.
 */
export const useImpactSessionID = () => {
  const cachedSessionID = useRef<UUID | undefined>(undefined);

  const set = useCallback((sessionID: UUID) => {
    // Expire impact session ID in 1 month's time
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    const expiry = date.toUTCString();

    if (canReadWriteCookies()) {
      document.cookie = `_sws_imsessid=${sessionID}; path=/; expires=${expiry}`;
    }

    cachedSessionID.current = sessionID;
  }, []);

  const get = useCallback(() => {
    if (cachedSessionID.current !== undefined) {
      return cachedSessionID.current;
    }

    const impactSessionID = getCookie('_sws_imsessid') as UUID;

    if (impactSessionID !== undefined) {
      cachedSessionID.current = impactSessionID;
      return impactSessionID;
    }

    const newSessionID = uuidv4() as UUID;

    set(newSessionID);

    return newSessionID;
  }, [set]);

  return { get };
};

/**
 * Generate a unique event ID.
 */
export const generateEventID = () => {
  return uuidv4() as EventUUID;
};
